/**
 * @file     rentVsBuyCalc.ts
 * @purpose  Pure math library for rent-vs-buy break-even analysis. Payment and
 *           periodic rate come from core/finance (Canadian semi-annual
 *           compounding); the default rate is the posted five-year rate (D7).
 */
import { POSTED_FIVE_YEAR_RATE, amortizedPayment, canadianPeriodicRate } from '@/core/finance/mortgage';

/** All inputs needed for the rent-vs-buy calculation. */
export type RentVsBuyInputs = {
  /** Current monthly rent in CAD */
  monthlyRent: number;
  /** Annual rent inflation rate (e.g. 0.03 for 3%) */
  rentInflationPct: number;
  /** Purchase price of home in CAD */
  homePrice: number;
  /** Down payment as decimal (e.g. 0.20 for 20%) */
  downPaymentPct: number;
  /** Annual mortgage rate as a decimal (POSTED_FIVE_YEAR_RATE by default) */
  mortgageRatePct: number;
  /** Amortization in years */
  amortizationYears: number;
  /** Annual property tax rate as decimal (e.g. 0.005 for 0.5%) */
  propertyTaxPct: number;
  /** Monthly strata/condo fee in CAD */
  strataMonthly: number;
  /** Annual maintenance cost as decimal of home value (e.g. 0.01 for 1%) */
  maintenancePct: number;
  /** Annual home appreciation rate as decimal (e.g. 0.04 for 4%) */
  appreciationPct: number;
  /** Annual return rate if down payment invested instead (opportunity cost) */
  opportunityReturnPct: number;
  /** How many years the buyer plans to stay */
  stayYears: number;
  /** Closing costs as decimal of purchase price (e.g. 0.015 for 1.5%) */
  closingCostPct: number;
  /** Selling costs as decimal of sale price (e.g. 0.04 for 4%) */
  sellingCostPct: number;
};

/** Year-by-year comparison row. All values in CAD. */
import type { YearRow } from '@/types/finance';
export type { YearRow };

/** Result of the rent-vs-buy analysis. */
export type RentVsBuyResult = {
  yearByYear: YearRow[];
  /** Year at which buying becomes cheaper than renting, or null if it never crosses */
  breakevenYear: number | null;
  /** Buying is clearly better, clearly worse, or within 5% of each other */
  verdict: 'buy' | 'rent' | 'close';
  /** Net CAD savings from buying vs renting over stayYears (positive = buying saves money) */
  netSavings: number;
  /** Confidence based on how far apart the outcomes are */
  confidence: 'high' | 'medium' | 'low';
};

/**
 * Core rent-vs-buy analysis.
 * Returns year-by-year comparison, breakeven year, verdict, and confidence.
 */
export const rentVsBuyCalc = (inputs: RentVsBuyInputs): RentVsBuyResult => {
  const {
    monthlyRent,
    rentInflationPct,
    homePrice,
    downPaymentPct,
    mortgageRatePct,
    amortizationYears,
    propertyTaxPct,
    strataMonthly,
    maintenancePct,
    appreciationPct,
    opportunityReturnPct,
    stayYears,
    closingCostPct,
    sellingCostPct,
  } = inputs;

  const downPayment = homePrice * downPaymentPct;
  const principal = homePrice - downPayment;
  const closingCosts = homePrice * closingCostPct;
  const monthlyRate = canadianPeriodicRate(mortgageRatePct, 12);
  const payment = amortizedPayment(principal, mortgageRatePct, amortizationYears * 12);

  // Track running state
  let balance = principal;
  let cumRentCost = 0;
  let cumBuyCost = closingCosts + downPayment; // Upfront cost of buying (opportunity cost basis)
  let currentMonthlyRent = monthlyRent;
  let opportunityBalance = downPayment + closingCosts; // Lost investment opportunity

  const yearByYear: YearRow[] = [];
  let breakevenYear: number | null = null;

  for (let y = 1; y <= stayYears; y++) {
    // Rent side: rent inflates each year
    const yearRentCost = currentMonthlyRent * 12;
    cumRentCost += yearRentCost;
    currentMonthlyRent *= 1 + rentInflationPct;

    // Buy side: mortgage payments for this year
    let yearInterest = 0;
    let yearPrincipal = 0;
    for (let m = 0; m < 12; m++) {
      if (balance <= 0) break;
      const interestPayment = balance * monthlyRate;
      const principalPayment = Math.min(payment - interestPayment, balance);
      yearInterest += interestPayment;
      yearPrincipal += principalPayment;
      balance = Math.max(0, balance - principalPayment);
    }
    const yearPropertyTax = homePrice * propertyTaxPct;
    const yearMaintenance = homePrice * maintenancePct;
    const yearStrata = strataMonthly * 12;
    const yearBuyCost = yearInterest + yearPropertyTax + yearMaintenance + yearStrata;
    cumBuyCost += yearBuyCost;

    // Opportunity cost: the down payment + closing costs compounding at market rate
    opportunityBalance *= 1 + opportunityReturnPct;

    // Home value after appreciation
    const homeValue = homePrice * Math.pow(1 + appreciationPct, y);
    const sellingCosts = homeValue * sellingCostPct;
    const equity = homeValue - balance - sellingCosts;

    // Net buy cost = cash spent (upfront plus running) minus equity recovered,
    // plus only the forgone growth on the upfront cash. Closing costs are
    // already in cumBuyCost, so the opportunity term adds its growth alone.
    const netBuyCost = cumBuyCost - equity + opportunityBalance - downPayment - closingCosts;

    yearByYear.push({
      year: y,
      cumRentCost,
      cumBuyCost,
      equity,
      netBuyCost,
    });

    // Breakeven: first year where net buy cost drops below cumulative rent cost
    if (breakevenYear === null && netBuyCost <= cumRentCost) {
      breakevenYear = y;
    }
  }

  const lastRow = yearByYear[yearByYear.length - 1];
  const netSavings = lastRow ? lastRow.cumRentCost - lastRow.netBuyCost : 0;
  const pctDiff = lastRow && lastRow.cumRentCost > 0 ? Math.abs(netSavings) / lastRow.cumRentCost : 0;

  let verdict: RentVsBuyResult['verdict'];
  if (netSavings > 0 && pctDiff > 0.05) {
    verdict = 'buy';
  } else if (netSavings < 0 && pctDiff > 0.05) {
    verdict = 'rent';
  } else {
    verdict = 'close';
  }

  let confidence: RentVsBuyResult['confidence'];
  if (pctDiff > 0.20) {
    confidence = 'high';
  } else if (pctDiff > 0.08) {
    confidence = 'medium';
  } else {
    confidence = 'low';
  }

  return { yearByYear, breakevenYear, verdict, netSavings, confidence };
};

/**
 * Area market stats shape: mirrors the market_stats Supabase table columns used by the calc.
 * `area_key` matches the `area` step option values in rentVsBuyFlow.ts.
 */
export type AreaStats = {
  area_key: string;
  median_sale_price: number | null;
  median_rent_2br: number | null;
  price_appreciation_5yr: number | null;
  avg_property_tax_rate: number | null;
  avg_strata_monthly: number | null;
};

/**
 * Derive a comfortable purchase price range from monthly rent using Vancouver rent-to-price ratios.
 * Uses a 200–240× multiplier (Vancouver baseline gross rent multiplier range).
 * Optionally cross-checks against a 28% GDS affordability rule using the area mortgage rate.
 *
 * Returns { low, mid, high } in CAD so the narrative can show "roughly $X to $Y".
 */
export const deriveAffordablePrice = (
  monthlyRent: number,
  areaStats: Pick<AreaStats, 'median_sale_price'> | null,
  assumedMortgageRate = POSTED_FIVE_YEAR_RATE,
): { low: number; mid: number; high: number } => {
  // Rent-to-price multiplier approach (Vancouver gross rent multiplier 200–240×)
  const low = Math.round(monthlyRent * 200 / 50_000) * 50_000;
  const high = Math.round(monthlyRent * 240 / 50_000) * 50_000;
  const mid = Math.round((low + high) / 2 / 50_000) * 50_000;

  // If area median price is available, cap high at 120% of area median to stay grounded
  if (areaStats?.median_sale_price) {
    const cap = areaStats.median_sale_price * 1.2;
    return {
      low,
      mid: Math.min(mid, areaStats.median_sale_price),
      high: Math.min(high, Math.round(cap / 50_000) * 50_000),
    };
  }

  // Suppress unused-variable warning for assumedMortgageRate: reserved for future GDS check
  void assumedMortgageRate;

  return { low, mid, high };
};

/** Default inputs for Vancouver market (placeholder: replace with REBGV/CMHC data). */
export const DEFAULT_INPUTS: RentVsBuyInputs = {
  monthlyRent: 2800,
  rentInflationPct: 0.04,
  homePrice: 850000,
  downPaymentPct: 0.20,
  mortgageRatePct: POSTED_FIVE_YEAR_RATE,
  amortizationYears: 25,
  propertyTaxPct: 0.005,
  strataMonthly: 600,
  maintenancePct: 0.01,
  appreciationPct: 0.04,
  opportunityReturnPct: 0.07,
  stayYears: 10,
  closingCostPct: 0.015,
  sellingCostPct: 0.04,
};
